import { useMemo, useState } from 'react';

import { AppCard } from '@components/app-card';
import { Button } from '@components/button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTeam } from '@components/icons/team';
import { SearchBox } from '@components/search-box';
import { Sidebar, useSidebar } from '@components/sidebar';
import { CloseButton } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { UserCard } from '@components/user-card/user-card';
import { useScopedTranslate } from '@locale/translate';

import { ApplicationFollower, Follower } from 'api/jobs/requests';
import { useAuthenticatedUser } from 'features/authentication';
import { sortBy } from 'helpers/sorting';
import { useStore } from 'models/Provider';
import { IUser } from 'models/types';
import { filterUsers } from 'models/users/UserStore';

export const useAddToHiringTeamSidebar = (
  followerIds: number[],
  headerText: string,
  onAdd: (user: Follower | ApplicationFollower) => void
) => {
  const sidebar = useSidebar(
    () => (
      <AddToHiringTeamSidebar
        currentFollowerIds={followerIds}
        onAdd={onAdd}
        headerText={headerText}
      />
    ),
    {
      variant: 'REGULAR',
    }
  );
  return sidebar;
};

const AddToHiringTeamSidebar = ({
  currentFollowerIds,
  onAdd,
  headerText,
}: {
  currentFollowerIds: number[];
  onAdd: (user: Follower | ApplicationFollower) => void;
  headerText: string;
}) => {
  const { t } = useScopedTranslate(addToHiringTeamSidebarLocale);
  const { current, userStore } = useStore();
  const me = useAuthenticatedUser();
  const [search, setSearch] = useState('');
  const [addedFollowerIds, setAddedFollowerIds] = useState<number[]>([]);
  const filtered = useMemo(() => {
    let users = userStore.list.filter(
      (user: IUser) =>
        user.role !== 'user' &&
        !currentFollowerIds.includes(user.id) &&
        !addedFollowerIds.includes(user.id)
    );
    if (me.role !== 'superadmin') {
      users = users.filter((user: IUser) => user.id !== me.id);
    }
    if (search && search.length >= 3) {
      return filterUsers(users, search);
    }
    return sortBy(users, 'name');
  }, [search, userStore.list, currentFollowerIds, me.id, me.role, addedFollowerIds]);
  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack space={4}>
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Stack space={2} orientation="horizontal" align="center">
              <IconSelectSingle color="purple50" />
              <Sidebar.HeaderTitle title={headerText} />
            </Stack>
            <CloseButton />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={6}>
          <SearchBox
            onChange={setSearch}
            placeholder={
              current.info.isEnterprise ? t('searchPlaceholderEnterprise') : t('searchPlaceholder')
            }
          />
          <Stack space={2}>
            {filtered.length === 0 && (
              <AppCard variant="gray" padding="lg">
                <Stack space={2} orientation="horizontal">
                  <IconTeam />
                  <Text text={t('allAdded')} />
                </Stack>
              </AppCard>
            )}
            {filtered.map((user: Follower | ApplicationFollower) => {
              return (
                <UserCard
                  key={user.email}
                  name={user.name}
                  picture={user.picture}
                  email={user.email}
                  role={user.role}
                  departments={user.departments?.map((department) => {
                    const defaultLanguage = department.translations.find(
                      (translation) => translation.language === current.defaultLanguage
                    );
                    return defaultLanguage?.title ?? department.translations[0]?.title;
                  })}
                  actions={
                    <Button
                      variant="ghost"
                      onClick={() => {
                        onAdd(user);
                        setAddedFollowerIds((prevState) => [...prevState, user.id]);
                      }}
                      iconLeft={<IconAddPlus />}
                      text={t('add')}
                    />
                  }
                />
              );
            })}
          </Stack>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const addToHiringTeamSidebarLocale = {
  en: {
    add: 'Add',
    allAdded: 'All team members added',
    searchPlaceholderEnterprise: 'Search name, email, role or department',
    searchPlaceholder: 'Search name, email or role',
  },
  is: {
    add: 'Bæta við',
    allAdded: 'Öllum notendum hefur verið bætt við',
    searchPlaceholderEnterprise: 'Leita að nafni, netfangi, hlutverki eða deild',
    searchPlaceholder: 'Leita að nafni, netfangi eða hlutverki',
  },
};
